/**
 * Location Detection Utility
 *
 * Detects user's country (US or India) based on browser timezone:
 * - IST timezone = India
 * - All other timezones = US
 *
 * Results are cached in localStorage for performance.
 */

export type Country = 'us' | 'india';

const STORAGE_KEY = 'user_country';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

interface LocationCache {
  country: Country;
  timestamp: number;
  method: 'timezone' | 'manual';
}

/**
 * Get cached country from localStorage
 */
function getCachedCountry(): Country | null {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) return null;

    const data: LocationCache = JSON.parse(cached);

    // Check if cache is still valid
    const age = Date.now() - data.timestamp;
    if (age > CACHE_DURATION) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return data.country;
  } catch (error) {
    console.warn('Failed to read location cache:', error);
    return null;
  }
}

/**
 * Save country to localStorage cache
 */
function cacheCountry(country: Country, method: 'timezone' | 'manual'): void {
  try {
    const data: LocationCache = {
      country,
      timestamp: Date.now(),
      method
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to cache location:', error);
  }
}

/**
 * Detect user's country
 * Now simplified to check cache only as we'll use a manual selection modal
 */
export async function detectUserCountry(): Promise<Country> {
  // Check cache first
  const cached = getCachedCountry();
  if (cached) {
    return cached;
  }

  // Default to US if nothing cached - the modal will override this
  return 'us';
}

/**
 * Manually set user's country (user override)
 */
export function setUserCountry(country: Country): void {
  cacheCountry(country, 'manual');

  // Dispatch custom event to notify components
  window.dispatchEvent(new CustomEvent('countryChanged', { detail: { country } }));
}

/**
 * Get current country from cache without detection
 */
export function getCurrentCountry(): Country | null {
  return getCachedCountry();
}

/**
 * Clear cached country data
 */
export function clearCountryCache(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Get country display name
 */
export function getCountryName(country: Country): string {
  return country === 'us' ? 'United States' : 'India';
}

/**
 * Get country flag emoji
 */
export function getCountryFlag(country: Country): string {
  return country === 'us' ? '🇺🇸' : '🇮🇳';
}

/**
 * Get currency symbol for country
 */
export function getCurrencySymbol(country: Country): string {
  return country === 'us' ? '$' : '₹';
}

/**
 * Get currency code for country
 */
export function getCurrencyCode(country: Country): string {
  return country === 'us' ? 'USD' : 'INR';
}
